import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { Database } from "@/integrations/supabase/types";

export type EffectiveRole =
  Database["public"]["Functions"]["resident_roles_effective"]["Returns"][number];
export type RoleType = Database["public"]["Tables"]["role_types"]["Row"];

/** Название из базы → как в интерфейсе («круг» в интерфейсе называется «сообщество»). */
export function displayRoleText(text: string | null | undefined): string {
  return (text ?? "")
    .replace(/Хранитель (круга|сообщества)/g, "Лидер")
    .replace(/Ведёт круг\. Роль действует, пока круг за человеком; переданные круги остаются в истории\./g,
      "Ведёт сообщество. Роль действует, пока сообщество за человеком; переданные сообщества остаются в истории.");
}

export function useResidentRoles(uid: string | null | undefined) {
  return useQuery({
    queryKey: ["resident-roles", uid],
    enabled: !!uid,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("resident_roles_effective", { uid: uid! });
      if (error) throw error;
      return ((data ?? []) as EffectiveRole[]).map((r) => ({ ...r, title: displayRoleText(r.title) }));
    },
  });
}

export function useRoleTypes() {
  return useQuery({
    queryKey: ["role-types"],
    staleTime: 30 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from("role_types").select("*").order("sort_order");
      if (error) throw error;
      return ((data ?? []) as RoleType[]).map((t) => ({
        ...t,
        title: displayRoleText(t.title),
        description: t.description ? displayRoleText(t.description) : t.description,
      }));
    },
  });
}

/** Active public roles only — for compact places (catalog, event card). */
export function usePublicActiveRoles(uid: string | null | undefined) {
  const { data: roles = [] } = useResidentRoles(uid);
  const { data: types = [] } = useRoleTypes();
  const pub = new Map(types.map((t) => [t.key, t.is_public]));
  return [...roles]
    .filter((r) => r.is_active && pub.get(r.role_key) !== false)
    .sort((a, b) => {
      const kindDiff = Number(a.kind === "title") - Number(b.kind === "title");
      if (kindDiff !== 0) return kindDiff;
      const orderDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0);
      if (orderDiff !== 0) return orderDiff;
      return a.role_key.localeCompare(b.role_key);
    });
}

export function roleLabel(r: EffectiveRole): string {
  let s = r.title;
  if (r.project_title) s += ` «${r.project_title}»`;
  if ((r.counter_value ?? 0) > 0) s += ` · ${r.counter_value}`;
  return s;
}

const monthYear = (d: string) =>
  new Intl.DateTimeFormat("ru-RU", { month: "long", year: "numeric" }).format(new Date(d));

export function revokedPeriod(r: EffectiveRole): string {
  const from = r.granted_at ? monthYear(r.granted_at) : "";
  const to = r.revoked_at ? monthYear(r.revoked_at) : "";
  if (from && to) {
    const [fm, fy] = from.split(" ");
    const [, ty] = to.split(" ");
    return fy === ty ? `${fm} — ${to}` : `${from} — ${to}`;
  }
  return from || to;
}

/** Clickable badge with description popover. `tone` = style on dark (passport) or light background. */
export function RoleBadge({
  role,
  tone = "dark",
  size = "md",
}: {
  role: EffectiveRole;
  tone?: "dark" | "light";
  size?: "sm" | "md";
}) {
  const dashed = role.duration === "one_time";
  const base =
    tone === "dark"
      ? `bg-white/15 text-white ${dashed ? "border border-dashed border-white/70" : "border border-white/25"} hover:bg-white/25`
      : `bg-td-teal/10 text-td-teal ${dashed ? "border border-dashed border-td-teal/60" : "border border-td-teal/20"} hover:bg-td-teal/15`;
  const sz = size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[12px]";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          className={`inline-flex max-w-full items-center gap-1 rounded-full font-medium transition ${base} ${sz}`}
        >
          {role.icon && <span className="shrink-0">{role.icon}</span>}
          <span className="truncate">{roleLabel(role)}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 text-sm" onClick={(e) => e.stopPropagation()}>
        <div className="font-semibold">
          {role.icon} {roleLabel(role)}
        </div>
        {role.subject_label && (
          <div className="mt-0.5 text-xs text-muted-foreground">{role.subject_label}</div>
        )}
        <p className="mt-2 text-[13px] leading-snug text-muted-foreground">
          {role.description || "Описание роли скоро появится."}
        </p>
      </PopoverContent>
    </Popover>
  );
}
